import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

// Tawk.to live chat — async loader with route-aware visibility, safe script
// sourcing, and a non-blocking offline fallback.
//
// Configuration:
//   VITE_TAWK_PROPERTY_ID  - Tawk property ID  (e.g. 64f0a1b2c3d4e5f6g7h8i9j0)
//   VITE_TAWK_WIDGET_ID    - Tawk widget ID    (defaults to "default")
//
// Both are publishable identifiers; safe to expose to the browser.
// If VITE_TAWK_PROPERTY_ID is missing, the component renders nothing
// (no script injected, no network requests, no console noise).
//
// Admin override: localStorage["peopels:tawk:disabled"] === "1" forces
// the widget off (used by /admin/tawk one-click disable).
//
// Safe sourcing: Tawk does not publish stable SRI hashes for the embed
// script (content changes when admins update the dashboard), so we cannot
// pin `integrity=`. Safety is enforced by (a) HTTPS-only origin allowlist
// in our CSP (see src/lib/security-headers.ts), (b) `crossorigin="*"`
// so the browser performs a CORS-checked fetch that participates in CSP,
// and (c) a runtime fallback that surfaces the offline message if the
// script is blocked or never reports ready.
//
// See docs/tawk-chat.md for full documentation.

const PROPERTY_ID = import.meta.env.VITE_TAWK_PROPERTY_ID as string | undefined;
const WIDGET_ID = (import.meta.env.VITE_TAWK_WIDGET_ID as string | undefined) ?? "default";
const DISABLE_FLAG = "peopels:tawk:disabled";

const HIDDEN_PATH_PATTERNS: RegExp[] = [
  /^\/jobs\/[^/]+\/confirm/i,
  /^\/bookings\/[^/]+\/dispute/i,
  /^\/auth(\/|$)/i,
  /^\/~oauth/i,
  /^\/api\//i,
];

function greetingForPath(path: string): string | null {
  if (path === "/" || path === "") {
    return "Need help? We're here to answer questions about bookings and services.";
  }
  if (/^\/(become-helper|helper\/onboard)/i.test(path)) {
    return "Need help getting verified? Our team is happy to help.";
  }
  return null;
}

export const OFFLINE_MESSAGE = "Leave us a message and we'll get back to you as soon as possible.";

type TawkAPI = {
  hideWidget?: () => void;
  showWidget?: () => void;
  maximize?: () => void;
  minimize?: () => void;
  toggle?: () => void;
  getStatus?: () => "online" | "away" | "offline" | undefined;
  setAttributes?: (attrs: Record<string, string>, cb?: (err?: unknown) => void) => void;
  onLoad?: () => void;
  onStatusChange?: (status: string) => void;
  onChatStarted?: () => void;
  visitor?: Record<string, string>;
  customStyle?: { offlineMessage?: string };
};

declare global {
  interface Window {
    Tawk_API?: TawkAPI;
    Tawk_LoadStart?: Date;
    __peopelsTawk?: {
      status: "loading" | "ready" | "failed";
      online: boolean | null;
    };
  }
}

function isHidden(path: string): boolean {
  return HIDDEN_PATH_PATTERNS.some((re) => re.test(path));
}

function isAdminDisabled(): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(DISABLE_FLAG) === "1";
  } catch {
    return false;
  }
}

export function TawkChat() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // null = unknown, true = online, false = offline / script failed
  const [online, setOnline] = useState<boolean | null>(null);
  const [scriptFailed, setScriptFailed] = useState(false);
  const [dismissedFallback, setDismissedFallback] = useState(false);

  // Inject the Tawk script exactly once.
  useEffect(() => {
    if (typeof window === "undefined" || !PROPERTY_ID) return;
    if (isAdminDisabled()) return;
    if (document.getElementById("tawk-to-script")) return;

    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();
    window.__peopelsTawk = { status: "loading", online: null };

    window.Tawk_API.customStyle = { offlineMessage: OFFLINE_MESSAGE };

    const api = window.Tawk_API;
    api.onLoad = () => {
      if (window.__peopelsTawk) window.__peopelsTawk.status = "ready";
      const status = api.getStatus?.();
      if (status) {
        const isOnline = status === "online";
        setOnline(isOnline);
        if (window.__peopelsTawk) window.__peopelsTawk.online = isOnline;
      }
    };
    api.onStatusChange = (status: string) => {
      const isOnline = status === "online";
      setOnline(isOnline);
      if (window.__peopelsTawk) window.__peopelsTawk.online = isOnline;
    };

    const s = document.createElement("script");
    s.id = "tawk-to-script";
    s.async = true;
    s.defer = true;
    s.src = `https://embed.tawk.to/${PROPERTY_ID}/${WIDGET_ID}`;
    s.charset = "UTF-8";
    s.setAttribute("crossorigin", "*");
    s.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
    s.onerror = () => {
      // CSP block, network failure, or ad blocker. Surface the offline
      // fallback so the visitor still has a path to contact us.
      if (window.__peopelsTawk) window.__peopelsTawk.status = "failed";
      setScriptFailed(true);
    };
    // If the script hasn't reported ready in 8s, assume it was blocked.
    const failTimer = window.setTimeout(() => {
      if (window.__peopelsTawk?.status !== "ready") {
        setScriptFailed(true);
      }
    }, 8000);

    document.body.appendChild(s);
    return () => window.clearTimeout(failTimer);
  }, []);

  // Route-aware visibility + per-route greeting.
  useEffect(() => {
    if (typeof window === "undefined" || !PROPERTY_ID) return;
    const api = window.Tawk_API;
    if (!api) return;

    const apply = () => {
      if (isHidden(pathname) || isAdminDisabled()) {
        api.hideWidget?.();
        return;
      }
      api.showWidget?.();
      const greeting = greetingForPath(pathname);
      if (greeting && api.setAttributes) {
        api.setAttributes({ "page-greeting": greeting, "page-path": pathname }, () => {});
      }
    };

    if (api.showWidget) {
      apply();
    } else {
      const prev = api.onLoad;
      api.onLoad = () => {
        try { prev?.(); } catch { /* noop */ }
        apply();
      };
    }
  }, [pathname]);

  // Non-blocking offline fallback. Renders a small, dismissible toast in
  // the bottom-left corner (the Tawk widget owns bottom-right) when:
  //   - the embed script failed to load (CSP / network / ad blocker), OR
  //   - Tawk reports agents offline.
  // It never traps focus, never overlays page content, and visitors can
  // dismiss it. On hidden routes (payment, dispute, auth) we render nothing.
  const showFallback =
    !!PROPERTY_ID &&
    !isAdminDisabled() &&
    !isHidden(pathname) &&
    !dismissedFallback &&
    (scriptFailed || online === false);

  if (!showFallback) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto fixed bottom-4 left-4 z-40 max-w-xs rounded-lg border border-border bg-background/95 p-3 text-xs shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:max-w-sm"
    >
      <div className="flex items-start gap-2">
        <span aria-hidden className="mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full bg-amber-500" />
        <div className="flex-1">
          <p className="font-medium text-foreground">Support is offline</p>
          <p className="mt-1 text-muted-foreground">{OFFLINE_MESSAGE}</p>
          <p className="mt-2">
            <a href="/contact" className="font-medium text-primary underline-offset-2 hover:underline">
              Send us a message →
            </a>
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDismissedFallback(true)}
          className="ml-1 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Dismiss offline notice"
        >
          ×
        </button>
      </div>
    </div>
  );
}
